import { setStatus } from '../scenario.slice';
import {
    IJson,
    IRootState,
    TCommonData,
    TInitWidgetState,
    TPayloadWidget,
    TTriggerWidgetAction,
    ValidationErrors,
    IJsonScenario,
    EStatus,
    TConfiguration,
} from '@libs/types';
import { isLoadingAction } from '@libs/utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJson } from '@libs/network';
import { initWidgetState, CFAccessDeniedError } from '@libs/network-utils';

type IPayloadScenario = {
    scenarioId: string;
    currentPageId: string;
    currentPage: Array<TPayloadWidget<TCommonData>>;
    configuration?: TConfiguration;
} & TInitWidgetState;

const getPayload = (scenario: IJsonScenario): IPayloadScenario => {
    const { mutableWidgetsMap, bottomSheetMap, modalWindowMap, internalIdWidgets, systemActions, submitWidget, rootWidgets } = initWidgetState(
        scenario.pages,
    ) as TInitWidgetState;
    return {
        scenarioId: scenario?.scenarioId,
        currentPageId: scenario?.pages?.[0]?.id,
        currentPage: scenario?.pages?.[0]?.widgets,
        configuration: scenario?.configuration ?? {},
        bottomSheetMap,
        mutableWidgetsMap,
        submitWidget,
        internalIdWidgets,
        modalWindowMap,
        systemActions,
        rootWidgets,
    };
};

export const fetchScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchScenario', async ({ action, widgetUid, isShowLoader, playerId, initPayerOptions }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        const json = await fetchJson(
            action,
            isShowLoader || isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            playerId,
            initPayerOptions,
        );
        if (json?.scenario) {
            return getPayload(json.scenario);
        }
        return thunkAPI.rejectWithValue({
            errorMessage: 'Response do not have any scenario!',
        });
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(e);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('Request was aborted by core');
        } else if (isCFAccessDeniedError) {
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
            });
        }
    }
});

export const fetchJsonAsync = createAsyncThunk<
    IJson | undefined,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchJsonAsync', async ({ action, isShowLoader, widgetUid, playerId }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        const json = await fetchJson(
            action,
            isShowLoader ?? isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            playerId,
        );
        if (json?.scenario) {
            return json;
        }
        return thunkAPI.rejectWithValue({
            errorMessage: 'Response do not have any scenario!',
        });
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('Request was aborted by core');
            throw e;
        } else if (isCFAccessDeniedError) {
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
            });
        }
    }
});

export const updateScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    IJson | undefined,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('updateScenario', async (json, thunkAPI) => {
    if (json?.scenario) {
        return getPayload(json.scenario);
    }
    return thunkAPI.rejectWithValue({
        errorMessage: 'Response do not have any scenario!',
    });
});
